/**
 * Supabase client — single typed instance for the entire app.
 * See ARCHITECTURE.md §4 for schema conventions.
 */

import { createClient } from '@supabase/supabase-js'
import { env } from '@/env'
import type { Database } from './db.types'

export const supabase = createClient<Database>(env.SUPABASE_URL, env.SUPABASE_ANON_KEY, {
  global: {
    // Resolve fetch lazily at call time instead of binding it at module load.
    // Identical behavior in the browser; required for MSW interception in tests.
    fetch: (input, init) => fetch(input, init),
  },
  auth: {
    flowType: 'pkce',
    autoRefreshToken: true,
    persistSession: true,
    // MUST stay false: AuthCallbackPage (/auth/callback) performs the PKCE
    // code exchange manually via exchangeCodeForSession(). If the SDK also
    // auto-detected the ?code= in the URL, it would consume the one-time auth
    // code + verifier first, leaving the manual call to fail with
    // "both auth code and code verifier should be non-empty". Both the Google
    // OAuth and email magic-link flows redirect to /auth/callback, so the
    // callback page is the single, authoritative place that exchanges the code.
    detectSessionInUrl: false,
  },
})

/**
 * Untyped Supabase client for dev/mock tables not yet in db.types.ts.
 * Phase 2 will generate proper types and this can be removed.
 * eslint-disable-next-line @typescript-eslint/no-explicit-any
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const supabaseUntyped = supabase as any as ReturnType<typeof createClient>
